import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut, RefreshCw, Plus, TrendingUp, Download, Upload, Shield, Flame } from 'lucide-react';
import { getPortfolio } from '../api/portfolio';
import { logout } from '../api/auth';
import PortfolioTable from './PortfolioTable';
import AddHoldingModal from './AddHoldingModal';
import ImportCSVModal from './ImportCSVModal';
import PortfolioSummary from './PortfolioSummary';
import PerformanceSummary from './PerformanceSummary';
import TopPositionsChart from './TopPositionsChart';
import PortfolioInsights from './PortfolioInsights';
import PortfolioAreaChart from './PortfolioAreaChart';
import FIRECalculator from './FIRECalculator';
import { exportPortfolioToCSV } from '../utils/csvExport';
import type { User } from '../types/auth';

interface DashboardProps {
  user: User;
  onLogout: () => void;
  onAdminClick?: () => void;
  onFIREClick?: () => void;
  currentView?: string;
  onViewChange?: (view: any) => void;
}

export default function Dashboard({ user, onLogout, onAdminClick, currentView, onViewChange }: DashboardProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: portfolio, isLoading, refetch } = useQuery({
    queryKey: ['portfolio'],
    queryFn: getPortfolio,
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      onLogout();
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-600 rounded-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Investment Tracker</h1>
                <p className="text-xs text-gray-500">Welcome back, {user.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="btn btn-secondary flex items-center gap-2"
                disabled={isLoading}
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Import CSV
              </button>
              <button
                onClick={() => portfolio && exportPortfolioToCSV(portfolio)}
                className="btn btn-secondary flex items-center gap-2"
                disabled={!portfolio || portfolio.length === 0}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Stock
              </button>
              <button
                onClick={() => onViewChange && onViewChange(currentView === 'fire' ? 'dashboard' : 'fire')}
                className={`btn flex items-center gap-2 ${
                  currentView === 'fire'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white border-orange-600'
                    : 'btn-secondary bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200'
                }`}
              >
                <Flame className="w-4 h-4" />
                FIRE
              </button>
              {onAdminClick && (
                <button
                  onClick={onAdminClick}
                  className="btn btn-secondary flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
              )}
              <button
                onClick={handleLogout}
                className="btn btn-secondary flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* FIRE Calculator View */}
        {currentView === 'fire' && portfolio && (
          <FIRECalculator
            currentPortfolioValue={portfolio.reduce((sum, h) => sum + (h.totalValueUSD || h.totalValue || 0), 0)}
            currency={user.preferred_currency || 'USD'}
          />
        )}

        {/* Dashboard View */}
        {currentView !== 'fire' && (
          <div className="space-y-6">
            {/* Portfolio Summary */}
            <PortfolioSummary portfolio={portfolio || []} />

            {/* Performance Summary - Gains/Losses */}
            <PerformanceSummary portfolio={portfolio || []} />

            {/* Area Chart - Stacked Growth with Time Periods */}
            <PortfolioAreaChart portfolio={portfolio || []} />

            {/* Top Positions Chart with ETF Untangling */}
            <TopPositionsChart portfolio={portfolio || []} />

            {/* AI Insights - Below Charts */}
            <PortfolioInsights />

            {/* Holdings Table */}
            <PortfolioTable portfolio={portfolio || []} />
          </div>
        )}
      </main>

      {/* Add Holding Modal */}
      {showAddModal && <AddHoldingModal onClose={() => setShowAddModal(false)} />}
      
      {/* Import CSV Modal */}
      {showImportModal && <ImportCSVModal onClose={() => setShowImportModal(false)} />}
    </div>
  );
}
